import { usePublicData } from "../hooks/usePublicData";
import { fetchNextUnlocks } from "../services/proficienciesService";
import { NextUnlock } from "../types/proficiencies";
import { stripAirframePrefix } from "../utils/proficiencyName";

const NEXT_UNLOCKS_LIMIT = 5;

const amount = (value: number) => (Math.round(value * 10) / 10).toLocaleString();

// A pilot's own list leads with the proficiency; the community list leads with the pilot, so the
// two read as different things even when they sit on the same page.
function Row({ item, showPilot, pilotLink }: { item: NextUnlock; showPilot: boolean; pilotLink: string }) {
  const name = stripAirframePrefix(item.display_name, item.airframe_display_name);
  const pilot = item.pilot_name || item.pilot_uid;
  return (
    <li className={"next-unlock" + (showPilot ? " is-community" : "")}>
      {showPilot
        ? <a className="next-unlock-title" href={`#/pilots/${encodeURIComponent(item.pilot_uid)}`}>{pilot}</a>
        : <a className="next-unlock-title" href={pilotLink}>{name}</a>}
      <span className="next-unlock-meta">
        {showPilot ? <>{name} · {item.airframe_display_name} · {item.tier.label}</> : <>{item.airframe_display_name} · {item.tier.label}</>}
      </span>
      <span className="next-unlock-progress">{amount(item.progress)} / {amount(item.tier.at)} {item.unit}</span>
      <progress aria-label={`${name}: ${item.tier.label}`} max={1} value={item.fraction} />
    </li>
  );
}

// A pilot's closest tiers, or with no pilot the community's (one entry per pilot). It is supporting
// content, so it stays out of the way when empty and when the request fails.
export default function NextUnlocks({ pilotUid, title = "Next up", heading = true, emptyMessage, limit = NEXT_UNLOCKS_LIMIT }: {
  pilotUid?: string | null;
  title?: string;
  heading?: boolean;
  emptyMessage?: string;
  limit?: number;
}) {
  const { data } = usePublicData(`next-unlocks:${pilotUid ?? ""}:${limit}`, () => fetchNextUnlocks({ pilotUid: pilotUid ?? undefined, limit }));
  const items = data?.items ?? [];
  if (items.length === 0) return data && emptyMessage ? <p className="dashboard-empty-row">{emptyMessage}</p> : null;

  return (
    <section className="next-unlocks" aria-label={title}>
      {heading && <h2 className="next-unlocks-title">{title}</h2>}
      <ul className="next-unlocks-list">
        {items.map((item) => (
          <Row
            key={`${item.pilot_uid}:${item.proficiency_id}`}
            item={item}
            showPilot={!pilotUid}
            pilotLink={`#/proficiencies?pilot=${encodeURIComponent(item.pilot_uid)}&airframe=${encodeURIComponent(item.group)}`}
          />
        ))}
      </ul>
    </section>
  );
}
